import axios from "axios";
import Axios from "Services/Auth/AxiosInterceptors";

/**
 * Bridges react-query's AbortSignal to axios's CancelToken.
 *
 * axios only learned about `signal` in 0.22 and this project is on 0.21.4, so
 * passing the signal straight through does nothing — the request runs to
 * completion and its response is simply discarded. React Query aborts the
 * signal as soon as a query loses its last observer (switching tab, moving the
 * date range), which is exactly when the in-flight request stops being wanted:
 * translating that into a real cancel is what keeps a slow previous query from
 * holding a connection, and from landing after the one the user is waiting for.
 *
 * Reading `signal` also matters on its own: React Query only arms automatic
 * cancellation for a query whose function actually touches it.
 */
const cancelWith = (signal) => {
  if (!signal) return {};
  if (signal.aborted) return { cancelToken: new axios.CancelToken((c) => c("cancelled")) };
  const source = axios.CancelToken.source();
  signal.addEventListener("abort", () => source.cancel("cancelled"));
  return { cancelToken: source.token };
};

/**
 * Marketing conversions funnel, grouped by traffic source / campaign / ad.
 * First-party data only: PreRegister touches (utm/gclid/fbclid captured on
 * the landing page) joined to registered users and their paid visits.
 *
 * @param {{ from?: string, to?: string, groupBy?: "source"|"campaign"|"content",
 *           tab?: string }} params
 *   from/to — ISO dates (inclusive) filtering by pre-registration date.
 *   groupBy — which touch dimension to group rows by (default "campaign").
 *   tab — optional channel scope, same keys as GetConversionsList. Omitted =
 *     all channels (the summary table); supplied = that tab's own funnel,
 *     which is what the per-campaign cards on a channel tab are built from.
 * @returns {Promise<{data: Array, totals: Object}>}
 *   rows: { key, impressions, visitors, preRegistered, verified, registered,
 *           paidPatients, visits, revenue, discountOffered, discountRedeemed }
 *   impressions/visitors come from the landing-impression beacon, not from
 *   pre-registrations, so they are a SUPERSET: a campaign that bought clicks
 *   and captured no emails appears as a row with impressions and zeroes
 *   everywhere else. totals.visitors is queried over the whole range rather
 *   than summed from the rows, because one person can appear under two
 *   campaigns.
 */
export const GetConversions = async (params, signal) => {
  const {
    data: { result },
  } = await Axios.get(`/admin/marketing/conversions/get`, { params, ...cancelWith(signal) });
  return result;
};

/**
 * Paginated pre-registration detail rows for one channel tab.
 *
 * @param {{ tab?: "facebook"|"instagram"|"google"|"seo"|"blog"|"qr"|"ai"|"other",
 *           page?: number, perPage?: number, from?: string, to?: string,
 *           campaign?: string }} params
 *   Rows are bucketed by the FIRST touch's channel (initial referrer) and
 *   returned CONVERSIONS FIRST (paid > registered > verified > code sent,
 *   newest within each band).
 *   "ai" = channel instacured-ai only; "other" = catch-all inversion (every
 *   channel without a named tab — direct visits, untagged referrers, ...).
 *   campaign — optional scope to one campaign of that tab ("__none__" for the
 *   untagged rows); omitted means every campaign.
 * @returns {Promise<{data: Array, total: number, page: number, perPage: number}>}
 *   rows: { username, createdAt, stateName, emailVerified, discountOffered,
 *           discountRedeemedAt, channel, registered, paidVisits, revenue,
 *           firstTouch, lastTouch } — touches carry every captured URL param.
 */
export const GetConversionsList = async (params, signal) => {
  const {
    data: { result },
  } = await Axios.get(`/admin/marketing/conversions/list`, { params, ...cancelWith(signal) });
  return result;
};

/**
 * Merged conversions + spend for the dashboard in ONE request, computed off a
 * single optimized funnel pass on the backend (replaces calling GetConversions
 * and GetSpend separately — the marketing queries that spiked Atlas CPU).
 *
 * @param {{ from?: string, to?: string, groupBy?: "source"|"campaign"|"content"|"channel" }} params
 *   groupBy drives only the conversions half; spend always covers campaign +
 *   content. from/to — ISO instants (inclusive) filtering by pre-registration
 *   date.
 * @returns {Promise<{ conversions: {data: Array, totals: Object},
 *   spend: {configured: boolean, range?: Object, totals?: Object,
 *   campaigns?: Array, ads?: Array, unmatched?: Object} }>}
 *   conversions rows: { key, preRegistered, verified, registered, paidPatients,
 *   visits, revenue, discountOffered, discountRedeemed }. spend campaigns/ads
 *   rows: { key, matchedBy, spend, impressions, clicks, paidPatients, revenue,
 *   cpa, roas }. spend.configured is false without a Meta token.
 */
export const GetMarketingDashboard = async (params, signal) => {
  const {
    data: { result },
  } = await Axios.get(`/admin/marketing/dashboard/get`, { params, ...cancelWith(signal) });
  return result;
};

/**
 * Meta ad spend joined to the funnel for a range (kept for the per-tab campaign
 * spend path; the all-channel dashboard now gets spend from GetMarketingDashboard
 * in a single call).
 */
export const GetSpend = async (params, signal) => {
  const {
    data: { result },
  } = await Axios.get(`/admin/marketing/spend/get`, { params, ...cancelWith(signal) });
  return result;
};

/**
 * Campaign/ad name + delivery status (ACTIVE/PAUSED/...) from the Meta
 * Marketing API, keyed by numeric id. Empty object when the backend has no
 * FB_MARKETING_TOKEN configured.
 */
export const GetAdInfo = async (ids) => {
  const {
    data: { result },
  } = await Axios.get(`/admin/marketing/ad-info`, { params: { ids } });
  return result;
};

/**
 * Admin postback switches (per-platform pause) plus the last run's summary.
 * Toggles gate sends only — eligibility keeps accruing while paused, so
 * re-enabling delivers the backlog on the next run.
 *
 * @returns {Promise<{metaEnabled: boolean, googleEnabled: boolean,
 *   capiConfigured: boolean, sheetConfigured: boolean, lastRun: Object|null}>}
 */
export const GetPostbackControls = async () => {
  const {
    data: { result },
  } = await Axios.get(`/admin/marketing/postback/controls`);
  return result;
};

/** Update one or both platform switches; returns the fresh controls. */
export const SetPostbackControls = async (body) => {
  const {
    data: { result },
  } = await Axios.post(`/admin/marketing/postback/controls`, body);
  return result;
};

/**
 * Trigger a postback run now (same service the nightly cron calls).
 * dryRun=true previews eligibility without sending or stamping anything.
 */
export const RunPostbacks = async (dryRun) => {
  const {
    data: { result },
  } = await Axios.post(`/admin/marketing/postback/run`, { dryRun });
  return result;
};

/**
 * Repair "(not set)" touches: removes the Play-referrer placeholder touches
 * stored between 2026-08-24 and the sanitizeTouch fix, re-filing the affected
 * conversions under their real campaign. dryRun=true previews the records.
 */
export const RepairNotSetTouches = async (dryRun) => {
  const {
    data: { result },
  } = await Axios.post(`/admin/marketing/touches/repair`, { dryRun });
  return result;
};

/**
 * Delivery ledger: every first-party conversion with its per-platform
 * postback outcome (eligible / sent / error), newest paid visit first.
 */
export const GetPostbackLog = async (params) => {
  const {
    data: { result },
  } = await Axios.get(`/admin/marketing/postback/log`, { params });
  return result;
};

/** Run history: one row per postback run (nightly or manual), newest first. */
export const GetPostbackRuns = async (params) => {
  const {
    data: { result },
  } = await Axios.get(`/admin/marketing/postback/runs`, { params });
  return result;
};

/**
 * Attribution waterfall: where paid conversions lose their ad click id.
 * Read-only diagnostic behind the Attribution tab.
 */
export const GetAttributionWaterfall = async (days = 14, signal) => {
  const {
    data: { result },
  } = await Axios.get(`/admin/marketing/attribution/waterfall`, { params: { days }, ...cancelWith(signal) });
  return result;
};

/**
 * First-party traffic report (Traffic tab) — pageviews and unique visitors by
 * page, referrer, channel, host and day.
 *
 * This is the site's analytics now that Beam is gone (the vendor dissolved).
 * It reads the same landing-impression collection the Impressions column
 * reads, but WITHOUT the hasQuery filter: the conversions funnel counts ad
 * arrivals, this counts everyone.
 *
 * @param {{ from?: string, to?: string }} params ISO dates (inclusive).
 * @returns {Promise<{
 *   totals: { pageviews, paidPageviews, visitors },
 *   limit: number,
 *   byPath|byReferrer|byChannel|byHost|byDay: { rows: Array<{key, pageviews, visitors}>, truncated: boolean }
 * }>} Each table is capped at `limit` rows; `truncated` says whether a tail
 *   was cut, so a partial table never reads as the whole picture.
 */
export const GetTraffic = async (params, signal) => {
  const {
    data: { result },
  } = await Axios.get(`/admin/marketing/traffic/get`, { params, ...cancelWith(signal) });
  return result;
};

/**
 * Quiz funnel behind the impressions: per key (campaign / grouping) how many
 * people answered q1..q4, qualified, or were turned away (under 18, state not
 * served). rows: [{ key, q1, q2, q3, q4, qualified, under18, stateNotServed }];
 * totals: the same shape for the whole scope.
 */
export const GetQuizFunnel = async (params, signal) => {
  const {
    data: { result },
  } = await Axios.get(`/admin/marketing/quiz/get`, { params, ...cancelWith(signal) });
  return result;
};

// ── Lapsed-registration 10%-off follow-up ─────────────────────────────────
// Patients who created an account and never opened a visit. Same control shape
// as the postbacks: the switch gates the recurring sweep only, manual runs
// always work, and a dry run shows exactly who would be mailed.

/** Switches, schedule, template readiness, and the last run's summary. */
export const GetFollowUpControls = async () => {
  const {
    data: { result },
  } = await Axios.get(`/admin/marketing/followup/controls`);
  return result;
};

/** Toggle the recurring sweep; returns the fresh controls. */
export const SetFollowUpControls = async (body) => {
  const {
    data: { result },
  } = await Axios.post(`/admin/marketing/followup/controls`, body);
  return result;
};

/**
 * Run the sweep now. dryRun=true returns the candidate list WITHOUT claiming,
 * stamping, granting the discount, or sending anything.
 */
export const RunFollowUp = async (dryRun) => {
  const {
    data: { result },
  } = await Axios.post(`/admin/marketing/followup/run`, { dryRun });
  return result;
};
